'use client';

import React from 'react';
import Link from 'next/link';
import { 
  Trash2, 
  Plus, 
  Minus, 
  ArrowRight, 
  ShoppingCart, 
  ShieldCheck, 
  Truck,
  CheckCircle2,
  Sparkles,
  Phone
} from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useCurrency } from '@/context/CurrencyContext';

export default function CartPage() {
  const { items, updateQuantity, removeItem, clearCart, totals, itemCount } = useCart();
  const { format } = useCurrency();

  const freeShippingThreshold = 10000;
  const currentSubtotal = totals.subtotal;
  const diffForFreeShipping = Math.max(0, freeShippingThreshold - currentSubtotal);
  const progressPercent = Math.min(100, (currentSubtotal / freeShippingThreshold) * 100);

  if (items.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-20 text-center space-y-5">
        <div className="w-20 h-20 rounded-3xl bg-sky-50 dark:bg-sky-950/60 border border-sky-100 dark:border-sky-900 flex items-center justify-center mx-auto text-sky-600 dark:text-sky-400 shadow-sm">
          <ShoppingCart className="w-10 h-10" />
        </div>
        <h1 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white">Shporta juaj është bosh</h1>
        <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 max-w-sm mx-auto leading-relaxed">
          Nuk keni shtuar ende asnjë produkt në shportë. Zbuloni gamën tonë të kondicionerëve inverter dhe pompave të nxehtësisë.
        </p>
        <div className="pt-2">
          <Link
            href="/shop"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-sky-600 hover:bg-sky-500 text-white font-extrabold text-xs sm:text-sm transition-all shadow-md shadow-sky-600/20"
          >
            Eksploro Katalogun e Produkteve
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  // Construct quick WhatsApp checkout message
  const waCartText = encodeURIComponent(
    `Përshëndetje DYRMA Climate! Dua të porosis artikujt e mëposhtëm nga shporta:\n` +
    items.map((i) => `• ${i.name} (Sasia: ${i.quantity}) - ${i.price * i.quantity} ALL`).join('\n') +
    `\n\nTotali: ${totals.totalAmount} ALL`
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 w-full space-y-8">
      {/* Checkout Progress Stepper */}
      <div className="flex items-center justify-center gap-3 sm:gap-6 text-xs font-bold text-slate-400 dark:text-slate-500 pb-2">
        <div className="flex items-center gap-2 text-sky-600 dark:text-sky-400">
          <span className="w-6 h-6 rounded-full bg-sky-600 text-white flex items-center justify-center text-xs font-bold">1</span>
          <span>Shporta</span>
        </div>
        <span className="w-8 h-[2px] bg-slate-200 dark:bg-slate-700"></span>
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400 flex items-center justify-center text-xs">2</span>
          <span>Të Dhënat & Dërgimi</span>
        </div>
        <span className="w-8 h-[2px] bg-slate-200 dark:bg-slate-700"></span>
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400 flex items-center justify-center text-xs">3</span>
          <span>Konfirmimi</span>
        </div>
      </div>

      <div className="flex items-center justify-between pb-4 border-b border-slate-200 dark:border-slate-800">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white">
            Shporta e Blerjeve
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Keni <strong>{itemCount}</strong> artikuj në shportën tuaj
          </p>
        </div>

        <button
          onClick={clearCart}
          className="text-xs text-rose-600 dark:text-rose-400 hover:underline flex items-center gap-1 font-bold"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Zbraz Shportën
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Cart Item List */}
        <div className="lg:col-span-8 space-y-4">
          {/* Free Shipping Progress Indicator */}
          <div className="bg-white dark:bg-slate-900 p-4 sm:p-5 rounded-3xl border border-slate-200/90 dark:border-slate-800 space-y-2.5 shadow-sm">
            <div className="flex justify-between items-center text-xs">
              <span className="font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                <Truck className="w-4 h-4 text-sky-600 dark:text-sky-400" />
                {diffForFreeShipping === 0 ? (
                  <span className="text-emerald-600 dark:text-emerald-400 font-extrabold flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" /> Keni fituar Transport FALAS!
                  </span>
                ) : (
                  <span>
                    Shtoni edhe <strong className="text-sky-600 dark:text-sky-400">{format(diffForFreeShipping)}</strong> për Transport FALAS
                  </span>
                )}
              </span>
              <span className="font-bold text-slate-400 text-[11px]">{Math.round(progressPercent)}%</span>
            </div>
            <div className="w-full bg-slate-100 dark:bg-slate-800 h-2.5 rounded-full overflow-hidden">
              <div
                className="bg-gradient-to-r from-sky-500 to-emerald-500 h-full rounded-full transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {items.map((item) => (
            <div
              key={item.id}
              className="bg-white dark:bg-slate-900 p-4 sm:p-5 rounded-3xl border border-slate-200/90 dark:border-slate-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm"
            >
              <div className="space-y-1">
                {item.brandName && (
                  <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider block">
                    {item.brandName}
                  </span>
                )}
                <h3 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white leading-snug">{item.name}</h3>
                <div className="text-xs text-slate-400 font-mono">SKU: {item.sku}</div>
                {item.coolingCapacityBtu && (
                  <div className="text-xs text-sky-600 dark:text-sky-400 font-bold">
                    {item.coolingCapacityBtu.toLocaleString()} BTU
                  </div>
                )}
              </div>

              {/* Quantity Controls & Price */}
              <div className="flex items-center justify-between sm:justify-end gap-6 w-full sm:w-auto pt-3 sm:pt-0 border-t sm:border-t-0 border-slate-100 dark:border-slate-800">
                <div className="flex items-center border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 p-0.5">
                  <button
                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                    className="p-1.5 rounded-lg hover:bg-white dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 transition-colors"
                  >
                    <Minus className="w-3.5 h-3.5" />
                  </button>
                  <span className="px-3 text-xs font-black text-slate-900 dark:text-white">{item.quantity}</span>
                  <button
                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                    className="p-1.5 rounded-lg hover:bg-white dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div className="text-right min-w-28">
                  <div className="text-base font-black text-slate-900 dark:text-white">
                    {format(item.price * item.quantity)}
                  </div>
                  <div className="text-[11px] text-slate-400 dark:text-slate-500 font-medium">
                    {format(item.price)} / copë
                  </div>
                </div>

                <button
                  onClick={() => removeItem(item.id)}
                  className="p-2 text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 transition-colors"
                  title="Hiq nga shporta"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          {/* Delivery & Assurance note */}
          <div className="p-4 sm:p-5 rounded-3xl bg-sky-50/60 dark:bg-sky-950/40 border border-sky-100 dark:border-sky-900/60 flex items-start gap-3.5 text-xs text-slate-600 dark:text-slate-300">
            <Truck className="w-5 h-5 text-sky-600 dark:text-sky-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-slate-900 dark:text-white font-bold block mb-0.5">Transport i sigurt në të gjithë Shqipërinë</strong>
              Dorëzimi bëhet brenda 24-48 orëve. Pagesa bëhet me para në dorëzim (Cash on Delivery) ose me transfertë bankare me faturë tatimore.
            </div>
          </div>
        </div>

        {/* Order Summary Sidebar */}
        <div className="lg:col-span-4 bg-white dark:bg-slate-900 p-6 sm:p-7 rounded-3xl border border-slate-200/90 dark:border-slate-800 shadow-sm space-y-6 sticky top-24">
          <h2 className="text-base font-extrabold text-slate-900 dark:text-white pb-3 border-b border-slate-100 dark:border-slate-800">
            Përmbledhja e Porosisë
          </h2>

          <div className="space-y-3 text-xs">
            <div className="flex justify-between text-slate-600 dark:text-slate-400">
              <span>Nëntotali (pa TVSH):</span>
              <span className="font-semibold text-slate-900 dark:text-white">{format(totals.subtotal - totals.taxAmount)}</span>
            </div>

            <div className="flex justify-between text-slate-600 dark:text-slate-400">
              <span>TVSH (20% e përfshirë):</span>
              <span className="font-semibold text-slate-900 dark:text-white">{format(totals.taxAmount)}</span>
            </div>

            <div className="flex justify-between text-slate-600 dark:text-slate-400">
              <span>Transporti:</span>
              <span className="font-bold text-slate-900 dark:text-white">
                {totals.isFreeShipping ? (
                  <span className="text-emerald-600 dark:text-emerald-400">FALAS</span>
                ) : (
                  format(totals.shippingCost)
                )}
              </span>
            </div>

            <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex justify-between items-baseline text-base font-extrabold text-slate-900 dark:text-white">
              <span>Totali:</span>
              <span className="text-xl font-black text-sky-600 dark:text-sky-400">{format(totals.totalAmount)}</span>
            </div>
          </div>

          <div className="space-y-2.5">
            <Link
              href="/checkout"
              className="w-full py-3.5 rounded-2xl bg-sky-600 hover:bg-sky-500 text-white font-extrabold text-sm flex items-center justify-center gap-2 shadow-lg shadow-sky-600/25 transition-all hover:scale-[1.02]"
            >
              Vazhdo te Pagesa (Checkout)
              <ArrowRight className="w-4 h-4" />
            </Link>

            <a
              href={`https://wa.me/355693673867?text=${waCartText}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-3 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center justify-center gap-2 transition-all shadow-md shadow-emerald-600/20"
            >
              <Phone className="w-4 h-4" />
              Porosit Drejtpërdrejt me WhatsApp
            </a>
          </div>

          <div className="text-[11px] text-slate-400 dark:text-slate-500 space-y-1.5 pt-2 border-t border-slate-100 dark:border-slate-800">
            <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
              <span>Blerje e sigurt dhe faturë tatimore me NIPT</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
              <span>Pajisje 100% origjinale me garanci prodhuesi</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
